"use client";

import { parseAsInteger, useQueryState } from "nuqs";

/**
 * The result list's current page, 1-based.
 *
 * The legacy app kept this (and the selected POI) in a hand-rolled `#hash`
 * parameter. Query params replace it for one architectural reason: a fragment
 * never reaches the server, so nothing in the hash can influence a server-rendered
 * page — and server rendering is why this app exists in this form. nuqs also
 * clears the param when it returns to its default, so a first-page URL stays clean.
 *
 * Pass `null` to reset to the first page.
 */
export function usePage() {
  return useQueryState("page", parseAsInteger.withDefault(1));
}

/**
 * Send the user back to the first page — what every change to the result set
 * has to do, since page 3 of the old results is rarely page 3 of the new ones.
 *
 * A no-op when already on page 1. Writing the default back is not free: it is
 * still a history entry and still a URL update racing with whatever change
 * prompted it, and the changes that call this — ticking a filter, accepting a
 * search term — write to the URL in the same breath.
 */
export function useResetPage(): () => void {
  const [page, setPage] = usePage();
  return () => {
    if (page !== 1) void setPage(null);
  };
}
